<template>
  <div class="leads-page">
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

    <!-- === FILTER ROW === -->
    <div class="filter-row" style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px;
                margin:0 .5rem 1.5rem .5rem; animation: fadeIn 0.4s ease;">
      <h1
        style="display:flex; align-items:center; gap:8px; font-size:1.2rem; font-weight:700; color:var(--text); margin:0;">
        <i class="bx bx-user-circle" style="font-size:1.4rem; color:var(--primary);"></i>
        Leads Overview
      </h1>

      <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
        <!-- ✅ Websites Dropdown -->
        <select v-model="selectedWebsite" style="padding:8px 12px; border:1px solid var(--border); border-radius:6px;
                       background:var(--card); color:var(--text); font-size:13px; min-width:160px;">
          <option disabled value="">-- Select Page --</option>
          <option v-for="web in websites" :key="web.profile_id" :value="web.profile_id">
            {{web.profile_username }}
          </option>
        </select>

        <!-- ✅ Status Dropdown -->
        <select v-model="selectedStatus" style="padding:8px 12px; border:1px solid var(--border); border-radius:6px;
                       background:var(--card); color:var(--text); font-size:13px; min-width:140px;">
          <option>All Status</option>
          <option>Pending</option>
          <option>Closed</option>
          <option>Follow-up</option>
        </select>

        <button @click="applyFilters" style="display:inline-flex; align-items:center; gap:6px; background:var(--primary);
                       color:#fff; border:none; border-radius:6px; padding:8px 16px;
                       font-size:13px; font-weight:600; cursor:pointer; transition:all 0.3s ease;">
          <i class="bx bx-search"></i> Apply
        </button>
        <button class="btn-outline" @click="doExport">
          <i class="bx bx-export"></i> Export
        </button>
      </div>
    </div>

    <!-- === KPI ROW === -->
    <div class="kpi-row" style="display:flex; flex-wrap:wrap; gap:20px; margin-bottom:4px;">
      <!-- ✅ Placeholder when loading -->
      <template v-if="isLoading">
        <div v-for="i in 4" :key="i" class="kpi-card placeholder-glow"
          style="flex:1; min-width:200px; height:80px; border-radius:var(--radius); background:var(--card); border:1px solid var(--border);">
          <div class="placeholder-line" style="width:60%; height:12px; margin:16px; border-radius:4px;"></div>
          <div class="placeholder-line" style="width:40%; height:18px; margin:16px; border-radius:4px;"></div>
        </div>
      </template>

      <!-- ✅ Real Data -->
      <template v-else>
        <div v-for="card in leadStats" :key="card.title" class="kpi-card" style="flex:1; min-width:200px; background:var(--card); border:1px solid var(--border);
                    border-radius:var(--radius); padding:16px 20px; display:flex;
                    justify-content:space-between; align-items:center; box-shadow:0 2px 6px rgba(0,0,0,0.05);
                    transition:all 0.3s ease;">
          <div>
            <h4 style="margin:0; font-size:14px; color:var(--muted); font-weight:500;">{{ card.title }}</h4>
            <div style="font-size:1.4rem; font-weight:700; color:var(--text);">{{ card.value }}</div>
          </div>
          <i :class="card.icon" style="font-size:1.6rem; color:var(--primary); opacity:0.9;"></i>
        </div>
      </template>
    </div>
    <span :class="'status1'" style="margin-bottom:1rem; border-radius:6px; font-size:0.8rem; font-weight:600;">
      Select a page from the dropdown to view leads received for that specific page.
    </span>
    <!-- === LEADS TABLE === -->
    <div class="table-box" style="width:100%; overflow-x:auto; background:var(--card);
         border:1px solid var(--border); border-radius:var(--radius); padding:16px;
         box-shadow:0 2px 6px rgba(0,0,0,0.05); animation: fadeIn 0.5s ease;">

      <div class="table-header" style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap;
                  gap:10px; margin-bottom:10px;">
        <h4 style="margin:0; font-size:1rem; color:var(--text); font-weight:600;">All Leads</h4>
        <div style="display:flex; align-items:center; gap:8px;">
          <div class="search-box" style="display:flex; align-items:center; background:var(--card); border:1px solid var(--border);
                      border-radius:6px; padding:6px 8px;">
            <i class="bx bx-search" style="color:var(--muted);"></i>
            <input type="text" v-model="searchQuery" placeholder="Search leads..."
              style="border:none; outline:none; background:transparent; font-size:13px; padding-left:6px; color:var(--text);">
          </div>
        </div>
      </div>

      <div style="overflow-x:auto; width:100%; -webkit-overflow-scrolling:touch;">
        <table style="width:100%; border-collapse:collapse; min-width:800px;">
          <thead>
            <tr style="background:#F9FAFB;">
              <th style="padding:10px; border-bottom:1px solid var(--border); text-align:left; color:var(--muted);">Name
              </th>
              <th style="padding:10px; border-bottom:1px solid var(--border); text-align:left; color:var(--muted);">
                Contact</th>
              <th
                style="padding:10px; width:20rem; border-bottom:1px solid var(--border); text-align:left; color:var(--muted);">
                Detail</th>
              <th style="padding:10px; border-bottom:1px solid var(--border); text-align:left; color:var(--muted);">
                Status</th>
              <th style="padding:10px; border-bottom:1px solid var(--border); text-align:left; color:var(--muted);">Date
              </th>
              <th style="padding:10px; border-bottom:1px solid var(--border); text-align:center; color:var(--muted);">
                Action</th>
            </tr>
          </thead>
          <tbody>
            <!-- ✅ Placeholder Rows -->
            <tr v-if="isLoading">
              <td colspan="6" class="placeholder-line" style="height:18px; border-bottom:1px solid var(--border);"></td>
            </tr>

            <!-- ✅ Empty -->
            <tr v-else-if="leads.length === 0">
              <td colspan="6" style="text-align:center; padding:20px; color:var(--muted); font-size:13px;">
                No leads found.
              </td>
            </tr>

            <!-- ✅ Lead Data -->
            <tr v-else v-for="(lead,index) in filteredLeads" :key="lead.id"
              style="transition:background 0.3s ease; cursor:pointer;" @mouseover="hoverRow = lead.id"
              @mouseleave="hoverRow = null" :style="{ background: hoverRow === lead.id ? '#F9FAFB' : 'transparent' }">
              <td style="padding:10px; border-bottom:1px solid var(--border);">{{ lead.name }}</td>
              <td style="padding:10px; border-bottom:1px solid var(--border);">{{ lead.email }}<br> {{ lead.source }}
              </td>

              <!--  -->
              <td style="padding:10px; border-bottom:1px solid var(--border);">{{ shortTxt(lead.desc) }}</td>
              <td style="padding:10px; border-bottom:1px solid var(--border);">
                <span :class="'status ' + lead.status.toLowerCase()" @click="chStatus(lead.id,lead.status,index)"
                  style="padding:4px 10px; border-radius:6px; font-size:0.8rem; font-weight:600;">
                  {{ lead.status }}
                </span>
              </td>
              <td style="padding:10px; border-bottom:1px solid var(--border);">{{ lead.date }}</td>
              <td style="padding:10px; border-bottom:1px solid var(--border); text-align:center;">
                <i class="bx bx-trash" style="cursor:pointer; color:#ef4444; font-size:1.2rem;"
                  @click="confirmDelete(lead.id)"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ✅ Pagination -->
      <div
        style="display:flex; justify-content:space-between; align-items:center; margin-top:14px; flex-wrap:wrap; gap:10px;">
        <span style="font-size:13px; color:var(--muted);">
          Showing {{ startIndex + 1 }}–{{ endIndex }} of {{ leads.length }} leads
        </span>
        <div style="display:flex; align-items:center; gap:6px;">
          <button @click="prevPage" :disabled="currentPage === 1" style="border:none; background:#F3F4F6; color:var(--text); padding:6px 10px;
                         border-radius:6px; cursor:pointer;">
            <i class='bx bx-chevron-left'></i>
          </button>
          <button style="border:none; background:var(--primary); color:#fff; padding:6px 12px;
                         border-radius:6px; font-weight:600;">{{ currentPage }}</button>
          <button @click="nextPage" :disabled="endIndex >= leads.length" style="border:none; background:#F3F4F6; color:var(--text); padding:6px 10px;
                         border-radius:6px; cursor:pointer;">
            <i class='bx bx-chevron-right'></i>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>


<script>
  import { getLeads, getLeadReport, deleteLead, exportLeads, updateLeadStatus } from "@/apis/leadapis";

  import { getProfiles, } from "@/apis/profileapi";
  import { mainBaseUrl } from "@/apis/appconst";
  import { useToast } from "vue-toastification";

  export default {
    name: "LeadsPage",

    data() {
      return {
        loading: false,
        base_url: "",
        selectedWebsite: "",
        selectedStatus: "All Status",
        searchQuery: "",

        currentPage: 1,
        perPage: 6,
        totalLeads: 0,

        hoverRow: null,
        leads: [],
        leadStats: [
          { title: "Total Leads", value: 0, icon: "bx bx-group" },
          { title: "Today's Leads", value: 0, icon: "bx bx-calendar" },
          { title: "Pending", value: 0, icon: "bx bx-time-five" },
          { title: "Closed", value: 0, icon: "bx bx-check-circle" },
        ],

        websites: [],
        isLoading: false,
        toast: useToast(),
      };
    },

    computed: {
      filteredLeads() {
        let data = this.leads;

        if (this.selectedStatus !== "All Status") {
          data = data.filter((lead) => lead.status === this.selectedStatus);
        }

        if (this.searchQuery.trim() !== "") {
          const q = this.searchQuery.toLowerCase();
          data = data.filter(
            (lead) =>
              lead.name.toLowerCase().includes(q) ||
              lead.email.toLowerCase().includes(q) ||
              lead.source.toLowerCase().includes(q)
          );
        }

        const start = (this.currentPage - 1) * this.perPage;
        const end = start + this.perPage;
        return data.slice(start, end);
      },

      startIndex() {
        return (this.currentPage - 1) * this.perPage;
      },
      endIndex() {
        return Math.min(this.startIndex + this.perPage, this.leads.length);
      },
    },

    methods: {
      shortTxt(desc) {
        // if(desc.length>100){
        //   return (desc+"").substring(0,100);
        // }

        return desc;
      },
      async chStatus(id, status, pos) {
        try {

          const res = await updateLeadStatus(id, (status == "Pending") ? "Closed" : "Pending");

          if (res.status) {
            this.leads[pos].status = (status == "Pending") ? "Closed" : "Pending";
          } else {
            this.toast.error("Failed to update");
          }
        } catch (err) {
          console.error("export error:", err);
          this.toast.error("Failed to update");
        }
      },
      async doExport() {
        try {
          const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

          const res = await exportLeads(userData.user_id);

          if (res.status) {
            this.toast.success("Items exported successfully!");
          } else {
            this.toast.error("Failed to export items");
          }
        } catch (err) {
          console.error("export error:", err);
          this.toast.error("Error exporting items");
        }
      },

      async fetchWebsites() {
        try {
          const ldata = JSON.parse(sessionStorage.getItem("ldata") || "{}");
          this.loading = true;
          const res = await getProfiles(ldata.user_id, "none", "");
          this.loading = false;
          if (res.status && res.records) {
            this.websites = res.records;
          } else {
            this.toast.warning("No websites found.");
          }
        } catch (err) {
          console.error(err);
          this.toast.error("Failed to fetch websites.");
        }
      },

      async fetchLeadReport() {
        try {
          const ldata = JSON.parse(sessionStorage.getItem("ldata") || "{}");
          this.loading = true;
          const res = await getLeadReport(
            ldata.user_id,
            this.selectedStatus !== "All Status" ? this.selectedStatus : "",
            this.selectedWebsite
          );
          this.loading = false;
          if (res.status) {
            this.leadStats = [
              { title: "Total Leads", value: res.total_leads, icon: "bx bx-group" },
              { title: "Today's Leads", value: res.today_leads, icon: "bx bx-calendar" },
              { title: "Pending", value: res.pending_leads, icon: "bx bx-time-five" },
              { title: "Closed", value: res.closed_leads, icon: "bx bx-check-circle" },
            ];
          }
        } catch (err) {
          console.error(err);
          this.toast.error("Failed to load lead report.");
        }
      },

      async fetchLeads() {
        try {
          this.isLoading = true;
          const ldata = JSON.parse(sessionStorage.getItem("ldata") || "{}");

          const res = await getLeads(
            ldata.user_id,
            this.currentPage - 1,
            this.searchQuery,
            this.selectedStatus !== "All Status" ? this.selectedStatus : "",
            this.selectedWebsite
          );

          if (res.status && res.records) {
            this.leads = res.records.map((l) => ({
              id: l.lead_id,
              name: l.lead_name,
              email: l.lead_email,
              desc: l.lead_desc,
              username: l.profile_username,
              source: l.lead_source + "",
              status: l.lead_status || "Pending",
              date: l.lead_datetime ? l.lead_datetime.split(" ")[0] : "-",
            }));
            this.totalLeads = res.total1 || this.leads.length;
          } else {
            this.leads = [];
            this.totalLeads = 0;
          }
        } catch (err) {
          console.error(err);
          this.toast.error("Error fetching leads.");
        } finally {
          this.isLoading = false;
        }
      },

      async confirmDelete(lead_id) {
        if (!confirm("Delete this lead permanently?")) return;
        try {
          const res = await deleteLead(lead_id);
          if (res.status) {
            this.toast.success("Lead deleted successfully!");
            await this.fetchLeads();
            await this.fetchLeadReport();
          } else {
            this.toast.warning(res.success || "Failed to delete lead.");
          }
        } catch (err) {
          console.error(err);
          this.toast.error("Error deleting lead.");
        }
      },

      async applyFilters() {
        this.currentPage = 1;
        await this.fetchLeadReport();
        await this.fetchLeads();
      },

      nextPage() {
        if (this.endIndex < this.leads.length) {
          this.currentPage++;
          this.fetchLeads();
        }
      },

      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.fetchLeads();
        }
      },
    },

    async mounted() {
      this.base_url = mainBaseUrl;
      await this.fetchWebsites();
      await this.fetchLeadReport();
      await this.fetchLeads();
    },
  };
</script>

<style scoped>
  /* === PLACEHOLDER SHIMMER === */
  .placeholder-glow {
    position: relative;
    overflow: hidden;
  }

  .placeholder-glow::after {
    content: "";
    position: absolute;
    top: 0;
    left: -150px;
    width: 150px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
    animation: shimmer 1.2s infinite;
  }

  .placeholder-line {
    background: var(--border);
    border-radius: 6px;
    animation: fadePulse 1.2s ease-in-out infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(0);
    }

    100% {
      transform: translateX(100%);
    }
  }

  @keyframes fadePulse {

    0%,
    100% {
      opacity: 0.6;
    }

    50% {
      opacity: 1;
    }
  }

  /* === ANIMATIONS === */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* === KPI Cards === */
  .kpi-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 24px;
  }

  .kpi-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 16px 20px;
    flex: 1;
    min-width: 220px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 12px rgba(0, 0, 0, 0.08);
  }

  .kpi-card h4 {
    margin: 0;
    font-size: 14px;
    color: var(--muted);
    font-weight: 500;
  }

  .kpi-card .value {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text);
  }

  .kpi-icon {
    font-size: 1.6rem;
    color: var(--primary);
    opacity: 0.85;
  }

  /* === TABLE STYLING === */
  .table-box {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    padding: 16px;
    overflow-x: auto;
    animation: fadeIn 0.4s ease;
  }

  .table-box table {
    width: 100%;
    border-collapse: collapse;
    min-width: 800px;
  }

  .table-box th {
    text-align: left;
    padding: 10px;
    font-size: 13px;
    font-weight: 600;
    color: var(--muted);
    border-bottom: 1px solid var(--border);
    background: #f9fafb;
  }

  .table-box td {
    padding: 10px;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
    color: var(--text);
    transition: background 0.25s ease;
  }

  .table-box tr:hover td {
    background: #f9fafb;
  }

  /* === STATUS PILL COLORS === */
  .status {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
  }

  .status1 {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 200;
    text-transform: capitalize;
    background: #FEF3C7;
    color: #92400E;
  }

  .status.pending {
    background: #FEF3C7;
    color: #92400E;
  }

  .status.closed {
    background: #DCFCE7;
    color: #166534;
  }

  .status.follow-up {
    background: #E0E7FF;
    color: #3730A3;
  }

  /* === SEARCH BOX === */
  .search-box {
    display: flex;
    align-items: center;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 6px 8px;
    transition: border 0.3s ease;
  }

  .search-box input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: var(--text);
    padding-left: 6px;
    width: 150px;
  }

  .search-box:hover {
    border-color: var(--primary);
  }

  /* === FILTER ROW === */
  .filter-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin: 0 .5rem 1.5rem .5rem;
    animation: fadeIn 0.4s ease;
  }

  .filter-row select,
  .filter-row button {
    height: 38px;
    border-radius: 6px;
    border: 1px solid var(--border);
    font-size: 13px;
    color: var(--text);
    background: var(--card);
    transition: all 0.3s ease;
  }

  .filter-row button {
    background: var(--primary);
    color: #fff;
    border: none;
    padding: 0 16px;
    font-weight: 600;
  }

  .filter-row button:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  /* === PAGINATION === */
  .table-box button {
    transition: all 0.3s ease;
  }

  .table-box button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  }

  /* === RESPONSIVE === */
  @media (max-width: 768px) {
    .kpi-row {
      flex-direction: column;
    }

    .filter-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .filter-row select,
    .filter-row button {
      width: 100%;
    }

    .search-box input {
      width: 100%;
    }
  }

  /* =====================================================
   📱 FINAL RESPONSIVE FIX — LEADS PAGE
   ===================================================== */

  @media (max-width: 1024px) {
    .filter-row {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
      margin: 0 0.5rem 1.2rem 0.5rem !important;
    }

    .filter-row h1 {
      font-size: 1.1rem !important;
    }

    .filter-row div {
      width: 100% !important;
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
    }

    .filter-row select,
    .filter-row button {
      flex: 1 1 48% !important;
      min-width: 150px !important;
      font-size: 13px !important;
      padding: 8px 10px !important;
    }

    .kpi-row {
      gap: 14px !important;
      margin-bottom: 18px !important;
    }

    .kpi-card {
      min-width: 100% !important;
      padding: 14px 16px !important;
    }
  }

  @media (max-width: 768px) {

    /* Filter row stack */
    .filter-row {
      flex-direction: column !important;
      align-items: flex-start !important;
      margin-bottom: 1.2rem !important;
      gap: 10px !important;
    }

    .filter-row select,
    .filter-row button {
      width: 100% !important;
    }

    /* Table box padding & scroll fix */
    .table-box {
      padding: 12px !important;
      margin: 0 0.5rem !important;
      border-radius: 8px !important;
      overflow-x: auto !important;
    }

    .table-box table {
      min-width: 700px !important;
    }

    /* Reduce bottom spacing */
    .table-box+div {
      margin-bottom: 2rem !important;
    }

    /* KPI cards stacked */
    .kpi-row {
      flex-direction: column !important;
      gap: 12px !important;
    }

    /* Add soft padding to KPI cards */
    .kpi-card {
      padding: 12px 14px !important;
    }

    /* Pagination stack */
    .table-box>div:last-child {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
    }

    /* Search input full width */
    .search-box {
      width: 100% !important;
    }

    .search-box input {
      width: 100% !important;
      font-size: 13px !important;
    }

    /* Add spacing below pagination */
    .leads-page {
      padding-bottom: 2.5rem !important;
    }
  }

  @media (max-width: 480px) {
    .filter-row {
      margin-bottom: 1rem !important;
    }

    .filter-row h1 {
      font-size: 1rem !important;
    }

    .filter-row select,
    .filter-row button {
      font-size: 12px !important;
      padding: 6px 10px !important;
    }

    .table-box {
      padding: 10px !important;
      margin: 0 0.3rem !important;
    }

    .kpi-card {
      padding: 10px 12px !important;
    }

    .table-box table {
      min-width: 600px !important;
    }

    .leads-page {
      padding-bottom: 2rem !important;
    }
  }
</style>